import React, { useCallback, useEffect, useState } from "react"
import { Box, IconButton, Card, CardHeader, CardContent, CardActions, Collapse, Grid, Typography, TextField, Button, CircularProgress } from "@material-ui/core"
import { CloudDownloadOutlined, FileTextOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons'
import { ADMIN_SAVE_EMAIL, send_command } from '../../components/api'
import { decodeEmailTemplateBody, encodeEmailTemplateBody } from '../../utils/decodeEmailTemplateBody'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import { ErrorPop } from "components/ErrorPop"
import { NewEmailDialog } from "./NewEmailDialog"
import { SuperAdminFold } from "./SuperAdminFold"

const renderEmail = (email_body) => {
    try {
        let body = email_body
        body = body.replaceAll('<%= it.tenant_img %>', "https://s3.us-west-2.amazonaws.com/cdn.dataprotected.com/Logo.png")
        return body
    } catch (ex) {
        console.error({ ex })
        return "!! Email body parse error !!"
    }
}

function flattenEmails(tree) {
    const flat = []
    Object.keys(tree || {}).forEach(type => {
        Object.keys(tree[type]).forEach(lang => {
            flat.push(tree[type][lang])
        })
    })
    return flat
}

export const Emails = ({ tenant_id, logo }) => {
    const [loading, setLoading] = useState(false)
    const [showAddEmail, setShowAddEmail] = useState(false)
    const [emails, setEmails] = useState(null)
    const [error, setError] = useState(null)
    const [notice, setNotice] = useState(null)

    const download = (body, title_prefix) => {
        const blob = new Blob([body], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `${title_prefix}${String(new Date().toISOString())}.txt`;
        link.href = url;
        link.click();
    }

    const fetchEmails = useCallback(() => {
        if (!tenant_id) return
        setLoading(true)
        send_command('ADMIN_GET_TENANT_EMAILS', { tenant_id })
            .then(emailList => {
                const tree = {}
                emailList.forEach(email => {
                    if (!tree[email.email_type]) tree[email.email_type] = {}
                    tree[email.email_type][email.language] = email
                })
                setEmails(tree)
                setError(null)
            })
            .catch(er => {
                setError(getApiErrorMessage(er, 'Failed to load emails'))
            })
            .finally(() => setLoading(false))
    }, [tenant_id])

    useEffect(() => {
        fetchEmails()
    }, [fetchEmails])

    const backup_emails = () => {
        let backup = ""
        flattenEmails(emails).forEach(email => {
            backup += "\n===================================================\n"
            backup += ` Type: ${email.email_type}\tLanguage: ${email.language}`
            backup += "\n===================================================\n"
            backup += `Subject: ${email.subject}\n`
            backup += `----------------Body---------------- \n${decodeEmailTemplateBody(email.body)}\n`
        })
        download(backup, "backup")
    }

    const export_all = () => {
        download(JSON.stringify(emails, null, 3), "export")
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <CardActions disableSpacing>
                    <Button variant="contained" endIcon={<CloudDownloadOutlined />} onClick={backup_emails} disabled={!emails}>Backup</Button>&nbsp;
                    <Button variant="contained" endIcon={<FileTextOutlined />} onClick={export_all} disabled={!emails}>Export all</Button>&nbsp;
                    <Button variant="contained" endIcon={<FileTextOutlined />} onClick={() => setShowAddEmail(true)}>Add Email</Button>&nbsp;
                    <Button variant="outlined" onClick={fetchEmails}>Refresh</Button>
                </CardActions>
            </Grid>
            {loading && <Grid item xs={12}><CircularProgress size={24} /></Grid>}
            {!loading && emails && Object.keys(emails).length > 0 && (
                <Grid item xs={12}>
                    {Object.keys(emails).map(email_type => (
                        <SuperAdminFold key={email_type} title={email_type} count={Object.keys(emails[email_type]).length}>
                            {Object.keys(emails[email_type]).map(language => {
                                const email = emails[email_type][language]
                                return (
                                    <EmailCard
                                        key={`${email_type}-${language}`}
                                        tenant_id={tenant_id}
                                        email={email}
                                        logo={logo}
                                        onChanged={fetchEmails}
                                        setNotice={setNotice}
                                        setError={setError}
                                    />
                                )
                            })}
                        </SuperAdminFold>
                    ))}
                </Grid>
            )}
            {!loading && (!emails || Object.keys(emails).length === 0) && (
                <Grid item xs={12}>No emails found</Grid>
            )}
            <NewEmailDialog
                showDialog={showAddEmail}
                closeDialog={setShowAddEmail}
                tenant_id={tenant_id}
                onCreated={() => {
                    setNotice('Email created')
                    fetchEmails()
                }}
            />
            <ErrorPop error={error} setError={setError} timeout={6000} />
            <ErrorPop error={notice} setError={setNotice} timeout={4000} />
        </Grid>
    )
}

export function EmailCard({ tenant_id, email, onChanged, setNotice, setError }) {
    const [show, setShow] = useState(false)
    const [body, setBody] = useState("")
    const [subject, setSubject] = useState("")
    const [bodyChanged, setBodyChanged] = useState(false)

    useEffect(() => {
        setBody(decodeEmailTemplateBody(email.body) || "Email missing or can't be decoded")
        setSubject(email.subject)
        setBodyChanged(false)
    }, [email])

    const save = () => {
        const email_record = {
            body: encodeEmailTemplateBody(body),
            subject,
            language: email.language,
            email_type: email.email_type,
        }
        ADMIN_SAVE_EMAIL(tenant_id, email_record)
            .then(() => {
                setBodyChanged(false)
                setNotice('Email saved')
                if (onChanged) onChanged()
            })
            .catch(err => {
                setError(getApiErrorMessage(err, 'Failed to save email'))
            })
    }

    const delete_email = () => {
        if (!window.confirm(`Delete ${email.email_type} : ${email.language}`)) return
        send_command('ADMIN_DELETE_EMAIL', {
            tenant_id,
            type: email.email_type,
            language: email.language,
        })
            .then(() => {
                setNotice('Email deleted')
                if (onChanged) onChanged()
            })
            .catch(err => {
                setError(getApiErrorMessage(err, 'Failed to delete email'))
            })
    }

    const download = () => {
        const blob = new Blob([decodeEmailTemplateBody(email.body)], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `${email.email_type}_${email.language}.html`;
        link.href = url;
        link.click();
    }

    return (
        <Card elevation={10} style={{ margin: '1em' }}>
            <CardHeader
                onClick={() => setShow(!show)}
                title={`Language: ${email.language}`}
                subheader={`Type: ${email.email_type}`}
            />
            <Collapse in={show}>
                <CardContent>
                    <Typography variant="body2" color="textSecondary">
                        Subject: <TextField fullWidth multiline onChange={(e) => { setBodyChanged(true); setSubject(e.target.value) }} value={subject} />
                    </Typography>
                    <Box component="section" sx={{ p: 2, border: '1px solid gray', mt: 2 }} height={600}>
                        <Grid container>
                            <Grid item lg={6}>
                                <Box component="section" sx={{ p: 2, border: '1px solid gray', overflowY: "scroll" }} height={540}>
                                    <TextField fullWidth multiline onChange={(e) => { setBodyChanged(true); setBody(e.target.value) }} value={body} />
                                </Box>
                            </Grid>
                            <Grid item lg={6}>
                                <Box component="section" sx={{ p: 2, border: '1px solid gray', overflowY: "scroll" }} height={540}>
                                    <div dangerouslySetInnerHTML={{ __html: renderEmail(body) }} />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
                <CardActions disableSpacing>
                    <IconButton aria-label="download" onClick={download}><CloudDownloadOutlined /></IconButton>
                    <IconButton aria-label="save" onClick={save} disabled={!bodyChanged}><SaveOutlined /></IconButton>
                    <IconButton aria-label="delete" onClick={delete_email}><DeleteOutlined /></IconButton>
                </CardActions>
            </Collapse>
        </Card>
    )
}
